// Perfil público (telas 7.5 e 7.5b): qualquer conta abre o de qualquer jogador, amigo ou não. De cima para baixo:
// cabeçalho (rosto, apelido, nível, plano, divisão, trio principal e a cidade com consentimento), desde quando joga e a
// ofensiva, Adicionar amigo, retrospecto, números e o placar contra você, conquistas, Liga da Semana, trios, lances,
// ligas e comunidades, contra você e, no fim, Reportar e Bloquear. Seção sem dado não aparece. Bloqueio nos dois
// sentidos: "Perfil indisponível". Convidado: apelido, rosto, retrospecto e o selo Convidado.
// No seu perfil (proprio), Editar perfil e Compartilhar perfil ficam no lugar dos botões de amizade e trio.
import { ligaNoAr } from '../comum/liga.ts';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { NOME_DO_LANCE, LANCES_DO_PERFIL, type PerfilNaGamificacao, rotuloDaDivisao } from '@truco6/gamificacao';
import { NOME_DO_PLANO } from '@truco6/planos';
import {
  type MotivoDenuncia, type PerfilPublico, type RespostaDoPerfil, bloquear, desfazerAmizade, lerPerfilPublico, reportarJogador,
} from '../comum/amigos.ts';
import { ErroDaApi } from '../comum/api.ts';
import { avisarNaTela } from '../comum/aviso.tsx';
import { BotaoAmizade } from '../comum/BotaoAmizade.tsx';
import { BotaoTrio } from '../comum/BotaoTrio.tsx';
import type { Sessao } from '../comum/conta.ts';
import { abrirPerfil, fecharPerfil, usePerfilAberto } from '../comum/perfilAberto.ts';
import { definirRelacao, sessaoDasRelacoes, useRelacao } from '../comum/relacoes.ts';
import { Retrospecto } from '../comum/Retrospecto.tsx';
import { ir, voltar } from '../comum/rotas.ts';
import { Rosto } from '../comum/Rosto.tsx';
import { Medalha, NOME_DO_NIVEL } from '../liga/badges.tsx';
import { Escudo } from '../liga/trios.tsx';
import { Moeda } from './planos-comum.tsx';
import { Tela, Topo } from './comum.tsx';

const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
// "2026-03" vira "mar/2026".
export function mesAno(desde: string): string {
  const [a, m] = desde.split('-');
  return `${MESES[Number(m) - 1] ?? m}/${a}`;
}
const DATA = new Intl.DateTimeFormat('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit' });
const MOTIVOS: [MotivoDenuncia, string][] = [['apelido', 'Apelido'], ['rosto', 'Rosto'], ['comportamento', 'Comportamento na mesa']];

function Secao({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <section className="cartao secao-perfil" aria-label={titulo}>
      <h2 className="rotulo">{titulo.toUpperCase()}</h2>
      {children}
    </section>
  );
}

function Conquistas({ badges }: { badges: PerfilNaGamificacao['badges'] }) {
  const [todas, setTodas] = useState(false);
  if (!badges.length) return null;
  return (
    <Secao titulo="Conquistas">
      <ul className="badges-perfil-publico">
        {badges.slice(0, 4).map((b) => (
          <li key={b.id}><Medalha b={b} tamanho={52} /><small>{b.nome}</small><small className="nivel">{NOME_DO_NIVEL[b.nivel]}</small></li>
        ))}
      </ul>
      {badges.length > 4 && <button className="link" onClick={() => setTodas(!todas)} aria-expanded={todas}>{todas ? 'Ver menos' : `Ver todas (${badges.length})`}</button>}
      {todas && (
        <ul className="lista-badges-perfil">
          {badges.map((b) => <li key={b.id}><Medalha b={b} tamanho={36} /><span>{b.nome}</span><small>{NOME_DO_NIVEL[b.nivel]}</small></li>)}
        </ul>
      )}
    </Secao>
  );
}

function Liga({ l }: { l: PerfilNaGamificacao['liga'] }) {
  if (!l.pontosTemporada && !l.posicaoSemana && !l.vezesTop10) return null;
  return (
    <Secao titulo={ligaNoAr() ? 'Liga da Semana' : 'Ranking'}>
      <dl className="numeros-perfil">
        <div><dt>Pontos na temporada</dt><dd>{l.pontosTemporada}</dd></div>
        {l.posicaoSemana && <div><dt>Nesta semana{l.posicaoSemana.escopo === 'cidade' ? ', na cidade' : ', no Brasil'}</dt><dd>{l.posicaoSemana.posicao}º</dd></div>}
        <div><dt>Vezes no Top 10</dt><dd>{l.vezesTop10}</dd></div>
      </dl>
    </Secao>
  );
}

function Trios({ trios }: { trios: PerfilNaGamificacao['trios'] }) {
  if (!trios.length) return null;
  return (
    <Secao titulo="Trios">
      <ul className="trios-perfil-publico">
        {trios.map((t) => (
          <li key={t.id}>
            <Escudo id={t.escudo} tamanho={36} />
            <span className="quem-trio">
              <b>{t.nome}</b>
              <small>{t.capitao ? 'Capitão' : 'Membro'} · {t.partidas} {t.partidas === 1 ? 'partida' : 'partidas'}{t.partidas ? ` · ${t.aproveitamento}%` : ''}</small>
            </span>
            <span className="rostos-trio">
              {t.membros.map((m) => <Rosto key={m.usuario} perfil={{ apelido: m.apelido, avatar: m.avatar }} usuario={m.usuario} cor="var(--line-600)" tamanho={28} />)}
            </span>
          </li>
        ))}
      </ul>
    </Secao>
  );
}

function Lances({ lances }: { lances: PerfilNaGamificacao['lances'] }) {
  const feitos = LANCES_DO_PERFIL.filter((l) => (lances[l] ?? 0) > 0);
  if (!feitos.length) return null;
  return (
    <Secao titulo="Lances">
      <ul className="lances-perfil">{feitos.map((l) => <li key={l}><span>{NOME_DO_LANCE[l]}</span><b>{lances[l]}x</b></li>)}</ul>
    </Secao>
  );
}

function ContraVoce({ p }: { p: PerfilPublico }) {
  const c = p.contraVoce;
  if (!c) return null;
  return (
    <Secao titulo="Contra você">
      <p className="placar-contra"><span>{p.apelido}</span><b>{c.dono} x {c.visitante}</b><span>Você</span></p>
      <ul className="ultimas-contra">
        {c.ultimas.map((x) => (
          <li key={x.partida}>
            <span className={'quadro ' + (x.venceu ? 'vitoria' : 'derrota')} aria-hidden="true">{x.venceu ? '✓' : '✕'}</span>
            <span>{DATA.format(new Date(x.terminadaEm))}</span>
            <b>{x.placar[0]} x {x.placar[1]}</b>
            <small>{x.venceu ? `${p.apelido} ganhou` : 'Você ganhou'}</small>
          </li>
        ))}
      </ul>
    </Secao>
  );
}

// Reportar (motivo) e Bloquear, com confirmação. Bloquear desfaz a amizade e o perfil vira "Perfil indisponível".
function ReportarEBloquear({ sessao, p, aoBloquear }: { sessao: Sessao; p: { usuario: string; apelido: string }; aoBloquear: () => void }) {
  const [folha, setFolha] = useState<'reportar' | 'bloquear' | null>(null);
  const [motivo, setMotivo] = useState<MotivoDenuncia | null>(null);
  const [ocupado, setOcupado] = useState(false);
  if (sessao.anonimo) return null;
  async function enviar() {
    if (!motivo) return;
    setOcupado(true);
    try { await reportarJogador(sessao, p.usuario, motivo); setFolha(null); avisarNaTela('Recebemos. Vamos olhar com atenção.'); } catch (e) {
      avisarNaTela(e instanceof ErroDaApi ? e.message : 'Não deu certo. Tente de novo.');
    } finally { setOcupado(false); }
  }
  async function confirmarBloqueio() {
    setOcupado(true);
    try { const r = await bloquear(sessao, p.usuario); definirRelacao(p.usuario, r.estado); setFolha(null); aoBloquear(); } catch (e) {
      avisarNaTela(e instanceof ErroDaApi ? e.message : 'Não deu certo. Tente de novo.');
    } finally { setOcupado(false); }
  }
  return (
    <div className="acoes-denuncia">
      <button className="btn2" onClick={() => { setMotivo(null); setFolha('reportar'); }}>Reportar</button>
      <button className="btn2 perigo" onClick={() => setFolha('bloquear')}>Bloquear</button>
      {folha && createPortal(
        <div className="dialogo-fundo" onClick={(ev) => { if (ev.target === ev.currentTarget) setFolha(null); }}>
          {folha === 'reportar' ? (
            <div className="dialogo" role="alertdialog" aria-modal="true" aria-labelledby="reportar-titulo">
              <h2 id="reportar-titulo">Reportar {p.apelido}</h2>
              <p>O que está errado? Uma denúncia por jogador. A gente olha e decide, sem punição automática.</p>
              <div className="motivos" role="radiogroup" aria-label="Motivo">
                {MOTIVOS.map(([m, t]) => (
                  <button key={m} role="radio" aria-checked={motivo === m} className={'btn2' + (motivo === m ? ' on' : '')} onClick={() => setMotivo(m)}>{t}</button>
                ))}
              </div>
              <button className="btn1 largo" disabled={!motivo || ocupado} onClick={() => void enviar()}>ENVIAR</button>
              <button className="btn2 largo" onClick={() => setFolha(null)}>Cancelar</button>
            </div>
          ) : (
            <div className="dialogo" role="alertdialog" aria-modal="true" aria-labelledby="bloquear-titulo">
              <h2 id="bloquear-titulo">Bloquear {p.apelido}?</h2>
              <p>Vocês deixam de ser amigos, os pedidos somem e um não vê mais o perfil, o status e o WhatsApp do outro. Bloquear não tira ninguém de uma mesa. Desbloquear fica em Configurações.</p>
              <button className="btn2 largo perigo" disabled={ocupado} onClick={() => void confirmarBloqueio()}>Bloquear</button>
              <button className="btn2 largo" onClick={() => setFolha(null)}>Cancelar</button>
            </div>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}

// Amigos: "Desfazer amizade", com confirmação. É silencioso para o outro.
function DesfazerAmizade({ sessao, p }: { sessao: Sessao; p: { usuario: string; apelido: string } }) {
  const r = useRelacao(p.usuario);
  const [confirmar, setConfirmar] = useState(false);
  if (r !== 'amigos') return null;
  return (
    <>
      <button className="link" onClick={() => setConfirmar(true)}>Desfazer amizade</button>
      {confirmar && createPortal(
        <div className="dialogo-fundo" onClick={(ev) => { if (ev.target === ev.currentTarget) setConfirmar(false); }}>
          <div className="dialogo" role="alertdialog" aria-modal="true" aria-labelledby="desfazer-amizade">
            <h2 id="desfazer-amizade">Desfazer amizade com {p.apelido}?</h2>
            <p>{p.apelido} não recebe aviso. Para voltar, um dos dois manda um pedido novo.</p>
            <button className="btn2 largo perigo" onClick={() => {
              setConfirmar(false);
              desfazerAmizade(sessao, p.usuario).then((x) => definirRelacao(p.usuario, x.estado), (e: Error) => avisarNaTela(e.message));
            }}>Desfazer amizade</button>
            <button className="btn2 largo" onClick={() => setConfirmar(false)}>Manter</button>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}

// O conteúdo do perfil, igual na tela /jogador/:id, na folha por cima da tela e no topo do seu perfil.
export function ConteudoDoPerfil({ sessao, usuario, proprio = false, extras }: { sessao: Sessao; usuario: string; proprio?: boolean; extras?: React.ReactNode }) {
  const [r, setR] = useState<RespostaDoPerfil | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  useEffect(() => {
    let vivo = true;
    setR(null);
    lerPerfilPublico(sessao, usuario).then((x) => { if (vivo) setR(x); }, (e: Error) => { if (vivo) setErro(e.message); });
    return () => { vivo = false; };
  }, [usuario, sessao.id]);
  if (erro) return <p className="erro" role="alert">{erro}</p>;
  if (!r) return <p className="carregando" role="status">Carregando...</p>;
  if ('indisponivel' in r) return <p className="cartao vazio-lista perfil-indisponivel">Perfil indisponível</p>;
  const cab = (
    <header className="cab-perfil">
      <Rosto perfil={{ apelido: r.apelido, avatar: r.avatar }} usuario={r.usuario} cor="var(--gold-600)" tamanho={72} coroa={'coroa' in r && !!r.coroa} />
      <div className="quem-perfil">
        <h1>{r.apelido}</h1>
        {r.convidado && <span className="selo-convidado">Convidado</span>}
        {!r.convidado && (
          <>
            <p className="linha-perfil">
              <span>Nível {r.nivel}</span>
              <span className="plano-perfil"><Moeda plano={r.plano} tamanho={16} />{NOME_DO_PLANO[r.plano]}</span>
              <span>{rotuloDaDivisao(r.divisao)}</span>
            </p>
            {r.trioPrincipal && <p className="linha-perfil trio-principal"><Escudo id={r.trioPrincipal.escudo} tamanho={22} />{r.trioPrincipal.nome}</p>}
            {r.cidade && <p className="linha-perfil">{r.cidade.nome} · {r.cidade.uf}</p>}
          </>
        )}
      </div>
    </header>
  );
  if (r.convidado) {
    return (
      <div className="perfil-publico">
        {cab}
        {!proprio && <div className="acoes-perfil"><BotaoAmizade usuario={r.usuario} apelido={r.apelido} /></div>}
        <section className="cartao" aria-label="Retrospecto"><Retrospecto usuario={r.usuario} apelido={r.apelido} /></section>
      </div>
    );
  }
  const contra = r.contraVoce;
  return (
    <div className="perfil-publico">
      {cab}
      <p className="desde-perfil">No Truco de 6 desde {mesAno(r.desde)}{r.ofensiva > 0 && <> · <span aria-hidden="true">🔥</span> {r.ofensiva} {r.ofensiva === 1 ? 'dia' : 'dias'} de ofensiva</>}</p>
      {proprio ? extras : (
        <div className="acoes-perfil">
          <BotaoAmizade usuario={r.usuario} apelido={r.apelido} />
          <BotaoTrio usuario={r.usuario} apelido={r.apelido} />
          <DesfazerAmizade sessao={sessao} p={r} />
        </div>
      )}
      <section className="cartao" aria-label="Retrospecto"><Retrospecto usuario={r.usuario} apelido={r.apelido} /></section>
      <dl className="cartao numeros-perfil" aria-label="Números">
        <div><dt>Partidas</dt><dd>{r.partidas}</dd></div>
        <div><dt>Aproveitamento</dt><dd>{r.aproveitamento}%</dd></div>
        {contra && <div><dt>Contra você</dt><dd>{contra.dono} x {contra.visitante}</dd></div>}
      </dl>
      {proprio && <button className="link ver-estatisticas" onClick={() => ir({ tela: 'estatisticas' })}>Ver suas estatísticas</button>}
      {!proprio && <Conquistas badges={r.badges} />}
      <Liga l={r.liga} />
      {!proprio && <Trios trios={r.trios} />}
      <Lances lances={r.lances} />
      {r.liga.melhorPosicaoTemporada !== null && (
        <Secao titulo="Ligas e comunidades">
          <p className="linha-liga"><b>{ligaNoAr() ? 'Liga da Semana' : 'Ranking da temporada'}</b><span>Melhor posição na temporada: {r.liga.melhorPosicaoTemporada}º</span></p>
        </Secao>
      )}
      <ContraVoce p={r} />
      {!proprio && <ReportarEBloquear sessao={sessao} p={r} aoBloquear={() => setR({ indisponivel: true })} />}
    </div>
  );
}

// /jogador/:id (o link de Compartilhar perfil).
export function PerfilPublicoTela({ sessao, id }: { sessao: Sessao; id: string }) {
  return (
    <Tela>
      <Topo titulo="Perfil" voltar={voltar} />
      <ConteudoDoPerfil sessao={sessao} usuario={id} proprio={id === sessao.id} />
    </Tela>
  );
}

// A folha por cima de qualquer tela (abrirPerfil), inclusive da sala e da mesa.
export function PerfilGlobal() {
  const u = usePerfilAberto();
  const s = sessaoDasRelacoes();
  useEffect(() => {
    if (!u) return;
    const f = (ev: KeyboardEvent) => { if (ev.key === 'Escape') fecharPerfil(); };
    addEventListener('keydown', f);
    return () => removeEventListener('keydown', f);
  }, [u]);
  if (!u || !s) return null;
  return createPortal(
    <div className="perfil-fundo" role="dialog" aria-modal="true" aria-label="Perfil do jogador">
      <div className="perfil-folha">
        <div className="topo-com-acao">
          <span className="rotulo">PERFIL</span>
          <button className="fechar-folha" aria-label="Fechar perfil" onClick={fecharPerfil}>×</button>
        </div>
        <ConteudoDoPerfil key={u} sessao={s} usuario={u} proprio={u === s.id} />
      </div>
    </div>,
    document.body,
  );
}

export { abrirPerfil };
